import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { getPayoffPayments as getPayoffPaymentsThunk } from 'thunks';
import { getPayoffPayments } from 'selectors/getPayoffPayments';
import { getApplicationData } from 'selectors/getApplicationData';
import { ApplicationStatusName } from 'enums/ApplicationStatusName';
import { PaymentStatus, PayoffPaymentResponse } from 'api/PayoffApi';
import { addWeeks, formatYearMonthDayToDate, startOfDay } from 'utils/dateUtils';

import { useNavigate } from 'hooks/useNavigate';
import { RoutePath } from 'enums/Routes';

import { IconName, VisualType, UpcomingItem } from './useUpcomingData';
import { formatUpcomingDate, getPayoffLabel } from './upcomingSectionUtils';

const STATUSES_TO_SHOW_PAYMENTS = [ApplicationStatusName.AwaitingFunding, ApplicationStatusName.Funded];

const PAYMENT_SENT_STATUSES = [PaymentStatus.Sent, PaymentStatus.SentViaACH];

export const useUpcomingPayments = (): UpcomingItem[] => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const applicationData = useSelector(getApplicationData);
  const { payments } = useSelector(getPayoffPayments);
  const { application } = applicationData ?? {};
  const { status: applicationStatus, id: applicationId, dateOfLoan } = application ?? {};

  useEffect(() => {
    if (applicationId && applicationStatus && STATUSES_TO_SHOW_PAYMENTS.includes(applicationStatus)) {
      dispatch(getPayoffPaymentsThunk(applicationId));
    }
  }, [applicationId, applicationStatus, dispatch]);

  const shouldShowPayment = (paymentResponse: PayoffPaymentResponse) => {
    const {
      status,
      payment: { estimatedCompletionDate },
    } = paymentResponse;

    const estimatedDate = estimatedCompletionDate
      ? formatYearMonthDayToDate(estimatedCompletionDate)
      : formatYearMonthDayToDate(dateOfLoan);

    if (!PAYMENT_SENT_STATUSES.includes(status)) return true;
    if (!estimatedDate) return false;

    const oneWeekFromEstimatedDate = addWeeks(estimatedDate, 1);
    const today = startOfDay(new Date());
    return oneWeekFromEstimatedDate >= today;
  };

  const mapPaymentsToUpcomingItem = (paymentResponse: PayoffPaymentResponse): UpcomingItem => {
    const {
      payment: { amount, firm = 'Unknown', logoUrl, id, estimatedCompletionDate },
      status,
    } = paymentResponse;
    const groupLabel = formatUpcomingDate(estimatedCompletionDate) ?? getPayoffLabel(dateOfLoan);
    const date = estimatedCompletionDate
      ? new Date(estimatedCompletionDate)
      : formatYearMonthDayToDate(dateOfLoan) ?? undefined;

    return {
      title: firm,
      description: status === PaymentStatus.SentViaACH ? 'Sent via ACH' : 'Direct Pay',
      value: amount / 100,
      visual: logoUrl ? { type: VisualType.Logo, url: logoUrl } : { type: VisualType.Icon, name: IconName.Transfer },
      date,
      groupLabel,
      onClick: () => navigate(RoutePath.MethodPayment.replace(':id', id) as RoutePath),
    };
  };

  return payments.filter(shouldShowPayment).map(mapPaymentsToUpcomingItem);
};
